import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useAtom } from "jotai";
import "~/css/reception/DeliveryMap.css";
import "~/css/reception/common.css";
import "~/css/reception/ReceptionButton.css";
import { useTranslation } from "react-i18next";
import { usePlayVoiceWithCooldown } from "~/components/hooks/usePlayVoiceWithCooldown";
import { languageAtom, receptionCompanyIdAtom } from "~/Atom";
import {
  getReceptionCommonWordsByLanguage,
  getScreenMenuButton,
  getScreenMenuWords,
  useInactivityTimeout,
} from "~/utils/receptionUtils";
import { useNavigate } from "react-router-dom";
import {
  getActivationData,
  getDeliveryMapImageCache,
  getReceptionCommonWord,
} from "~/utils/receptionDB";
import { apiClient, buildIfaceApiUrl, createIfaceHeaders } from "~/utils/apiClient";
import ReceptionCommunicatingIndicator from "~/components/ReceptionCommunicatingIndicator";
import { ButtonType, type ReceptionCommonWordEntity } from "~/utils/receptionDB.types";
import { runReceptionBack } from "~/utils/receptionNavigation";
import { ROUTE } from "~/constants/routes";
import ReceptionHeader from "~/components/ReceptionHeader";

const visitHistoryGeneralUrl = buildIfaceApiUrl("v1/receptions/visit-history/general");

/**
 * DeliveryMap画面（会社選択の可否判定・自動遷移タイマーを含む）
 * 配達マップを表示し、見出し文言の取得と音声案内を行い、戻る操作と会社選択をコールバックする
 */
const DeliveryMapPage: React.FC = () => {
  const { t } = useTranslation();
  const [language] = useAtom(languageAtom);
  const [receptionCompanyId] = useAtom(receptionCompanyIdAtom);
  const { playWithCooldown } = usePlayVoiceWithCooldown({
    cooldownMs: 2000,
  });

  const navigate = useNavigate();
  const handleBack = useCallback(() => {
    runReceptionBack({ kind: "router-history", navigate });
  }, [navigate]);
  const handleFinishReception = useCallback(() => {
    navigate(ROUTE.RECEPTION.LOADING, {
      state: { skipStartupModal: true },
    });
  }, [navigate]);

  const [receptionCommonWord, setReceptionCommonWord] = useState<
    ReceptionCommonWordEntity | undefined
  >(undefined);
  // IDBにキャッシュされたBlobから生成した一時URL。アンマウント時にrevokeする。
  const [deliveryMapImageObjectUrl, setDeliveryMapImageObjectUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const companyId = receptionCompanyId;

  const { stopTimer, startTimer } = useInactivityTimeout(navigate);

  const headerTitle = useMemo((): string => {
    return (
      getReceptionCommonWordsByLanguage(receptionCommonWord, language)?.deliveryMapMessage ?? ""
    );
  }, [receptionCommonWord, language]);

  useEffect(() => {
    const fetchReceptionCommonWords = async () => {
      try {
        const currentWord = await getReceptionCommonWord();
        setReceptionCommonWord(currentWord);
      } catch (err) {
        console.error("Failed to load reception common word:", err);
      }
    };

    void fetchReceptionCommonWords();
  }, []);

  useEffect(() => {
    const fetchAndPost = async () => {
      const menuButton = await getScreenMenuButton(companyId);
      await postReceptionVisitHistoryGeneral(menuButton);
    };
    void fetchAndPost();
  }, [companyId]);

  // IDBにキャッシュされたBlobから object URL を生成する。
  // 署名付きURLの期限切れに依存しない安定した表示を実現する。
  useEffect(() => {
    // companyId 切り替え時、新しい会社のキャッシュが無い場合に古い表示が残らないよう先にリセットする。
    setDeliveryMapImageObjectUrl(null);

    let cancelled = false;
    let createdUrl: string | null = null;

    const loadFromCache = async () => {
      try {
        // 各会社の画像は companyId（= menuButton.id）でキャッシュされている
        const cache = await getDeliveryMapImageCache(companyId);
        if (cancelled || !cache) return;
        // appSettings 更新時にURLが変わるとキャッシュも置き換わるため、
        // ここではキャッシュが存在すれば常に最新と見なして利用する。
        const url = URL.createObjectURL(cache.blob);
        // 非同期完了までにアンマウント/依存変更が走った場合は setState せずに即 revoke する。
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        createdUrl = url;
        setDeliveryMapImageObjectUrl(url);
      } catch (err) {
        console.error("[deliveryMapImage] failed to load cache:", err);
      }
    };

    void loadFromCache();

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [companyId]);

  const postReceptionVisitHistoryGeneral = async (
    menuButton?: Awaited<ReturnType<typeof getScreenMenuButton>>
  ): Promise<void> => {
    setIsLoading(true);
    stopTimer();
    try {
      const activationData = await getActivationData();
      if (!activationData?.isAppointmentHistoryEnabled) return;

      const notificationType = menuButton?.deliveryNotificationType;
      if (!notificationType) return;

      const buttonName = await getScreenMenuWords(companyId, language, "deliveryButtonTitle");
      if (typeof buttonName !== "string") return;

      await apiClient.post(
        visitHistoryGeneralUrl,
        {
          route: ButtonType.DELIVERY,
          buttonName,
          notificationType,
        },
        {
          headers: createIfaceHeaders(true),
        }
      );
    } catch (err) {
      console.error("Failed to post visit history for delivery:", err);
    } finally {
      startTimer();
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void playWithCooldown(headerTitle).catch((error) => {
      console.error("Failed to play delivery map:", error);
    });
  }, [playWithCooldown]);

  return (
    <div className="reception-delivery-map-page">
      <ReceptionHeader showBackButton onBackButtonClick={handleBack} />
      <div className="reception-delivery-map-screen">
        <div className="reception-delivery-map-screen__content">
          {isLoading && <ReceptionCommunicatingIndicator />}
          <div className="screen__text">
            <h2 className="screen__title">{headerTitle}</h2>
          </div>

          {deliveryMapImageObjectUrl ? (
            <div className="reception-delivery-map-screen__image-container">
              <img
                className="reception-delivery-map-screen__image"
                // IDBキャッシュのBlob URLのみを使用する。
                // 署名付きURLは期限切れで表示できない可能性があるためフォールバックしない。
                src={deliveryMapImageObjectUrl}
                alt="delivery-map"
              />
            </div>
          ) : (
            <div className="reception-delivery-map-screen__image-placeholder" />
          )}

          <div className="reception-delivery-map-screen__btn-container">
            <button
              type="button"
              className="text-btn gradient-primary-btn"
              onClick={handleFinishReception}
            >
              <span>{t("endReceptionButton")}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryMapPage;
